import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "../../../../../services/contactformServices";

import { useContactForm } from "../../../../../contexts/ContactFormContext";

import {
  filterMessages,
  getFallbackSectionId,
  getMessageId,
  getMessagesStats,
  isDemoMode,
  sortMessagesByDate,
} from "../utils/messagesSectionUtils";

export default function useMessagesSection(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const { setUnreadCountContactForm } = useContactForm();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [readFilter, setReadFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    isOpen: false,
    message: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const loadMessages = useCallback(async () => {
    if (!finalSectionId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getAllMessagesContactForm();
      const safeData = Array.isArray(data) ? data : [];

      const filtered = safeData.filter(
        (message) => Number(message.section_id) === Number(finalSectionId)
      );

      const sorted = sortMessagesByDate(filtered);

      setMessages(sorted);

      const unreadCount = sorted.filter((message) => !message.read_message).length;
      setUnreadCountContactForm?.(unreadCount);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages :", error);
      toast.error("Erreur lors de la récupération des messages.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId, setUnreadCountContactForm]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const stats = useMemo(() => getMessagesStats(messages), [messages]);

  const rows = useMemo(() => {
    return filterMessages(messages, searchTerm, readFilter);
  }, [messages, searchTerm, readFilter]);

  const openDrawer = (message) => {
    setDrawer({
      isOpen: true,
      message,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      isOpen: false,
      message: null,
    });
  };

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const toggleReadStatus = async (messageId) => {
    if (handleDemoClick()) return;

    try {
      const currentMessage = messages.find(
        (message) => String(getMessageId(message)) === String(messageId)
      );

      if (!currentMessage) {
        toast.error("Message introuvable.");
        return;
      }

      await markMessageContactForm(messageId);

      const nextReadValue = !currentMessage.read_message;

      setMessages((prevMessages) => {
        const nextMessages = prevMessages.map((message) =>
          String(getMessageId(message)) === String(messageId)
            ? { ...message, read_message: nextReadValue }
            : message
        );

        const unreadCount = nextMessages.filter(
          (message) => !message.read_message
        ).length;

        setUnreadCountContactForm?.(unreadCount);

        return nextMessages;
      });

      setDrawer((prev) => {
        if (!prev.message) return prev;

        if (String(getMessageId(prev.message)) !== String(messageId)) {
          return prev;
        }

        return {
          ...prev,
          message: {
            ...prev.message,
            read_message: nextReadValue,
          },
        };
      });

      toast.success(
        nextReadValue
          ? "Message marqué comme lu."
          : "Message marqué comme non lu."
      );
    } catch (error) {
      console.error("Erreur changement état lecture :", error);
      toast.error("Erreur lors de la mise à jour du message.");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer ce message ?");

    if (!confirmed) return;

    try {
      await deleteMessageContactForm(messageId);

      setMessages((prevMessages) => {
        const nextMessages = prevMessages.filter(
          (message) => String(getMessageId(message)) !== String(messageId)
        );

        const unreadCount = nextMessages.filter(
          (message) => !message.read_message
        ).length;

        setUnreadCountContactForm?.(unreadCount);

        return nextMessages;
      });

      setDrawer((prev) => {
        if (!prev.message) return prev;

        if (String(getMessageId(prev.message)) === String(messageId)) {
          return {
            isOpen: false,
            message: null,
          };
        }

        return prev;
      });

      toast.success("Message supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  return {
    finalSectionId,
    messages,
    rows,
    stats,
    loading,
    demoMode,

    searchTerm,
    setSearchTerm,
    readFilter,
    setReadFilter,

    drawer,
    openDrawer,
    closeDrawer,

    refreshData: loadMessages,
    toggleReadStatus,
    handleDeleteMessage,
  };
}